"use strict";
import {
  Action,
  AuthenticationTypes
} from "@uniswap/client-embeddedwallet/dist/uniswap/embeddedwallet/v1/service_pb";
import {
  fetchChallengeRequest,
  fetchCreateWalletRequest,
  fetchDeleteAuthenticatorRequest,
  fetchDisconnectRequest,
  fetchExportSeedPhraseRequest,
  fetchListAuthenticatorsRequest,
  fetchRegisterNewAuthenticatorRequest,
  fetchSignMessagesRequest,
  fetchSignTransactionRequest,
  fetchSignTypedDataRequest,
  fetchWalletSigninRequest
} from "data/rest/embeddedWallet/requests";
import { authenticatePasskey, registerPasskey } from "features/passkey/passkey";
import { isAddress } from "utilities/src/addresses";
import { logger } from "utilities/src/logger/logger";
export {
  Action,
  Authenticator,
  RegistrationOptions_AuthenticatorAttachment as AuthenticatorAttachment,
  AuthenticatorNameType
} from "@uniswap/client-embeddedwallet/dist/uniswap/embeddedwallet/v1/service_pb";
async function registerNewPasskey({
  username,
  authenticatorAttachment,
  action
} = {}) {
  const options = { authenticatorAttachment, username };
  try {
    const challenge = await fetchChallengeRequest({
      type: AuthenticationTypes.PASSKEY_REGISTRATION,
      action: action ?? Action.CREATE_WALLET,
      options
    });
    return await registerPasskey(challenge.challengeOptions);
  } catch (registrationError) {
    if (registrationError instanceof Error && registrationError.name === "AbortError") {
      logger.debug("embeddedWallet.ts", "registerNewPasskey", "User aborted registration");
    } else {
      logger.debug("embeddedWallet.ts", "registerNewPasskey", `Error during registration: ${registrationError}`);
    }
    throw registrationError;
  }
}
export async function createNewEmbeddedWallet(unitag) {
  try {
    const passkeyCredential = await registerNewPasskey({ username: unitag });
    if (!passkeyCredential) {
      return void 0;
    }
    const createWalletResp = await fetchCreateWalletRequest({ credential: passkeyCredential });
    if (createWalletResp?.walletAddress) {
      logger.debug(
        "embeddedWallet.ts",
        "createNewEmbeddedWallet",
        `New wallet created: ${createWalletResp.walletAddress}`
      );
      if (!isAddress(createWalletResp.walletAddress)) {
        logger.error(new Error("Invalid address returned from create wallet response"), {
          tags: {
            file: "embeddedWallet.ts",
            function: "createNewEmbeddedWallet"
          }
        });
        return void 0;
      }
      return createWalletResp.walletAddress;
    }
    return void 0;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "createNewEmbeddedWallet"
      }
    });
    throw error;
  }
}
export async function authenticateWithPasskey(action) {
  try {
    const challenge = await fetchChallengeRequest({
      type: AuthenticationTypes.PASSKEY_AUTHENTICATION,
      action
    });
    return await authenticatePasskey(challenge.challengeOptions);
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      logger.debug("embeddedWallet.ts", "authenticateWithPasskey", "User aborted the registration process");
    } else {
      logger.error(new Error("Error during registration", { cause: error }), {
        tags: {
          file: "embeddedWallet.ts",
          function: "authenticateWithPasskey"
        }
      });
    }
    throw error;
  }
}
export async function authenticateWithPasskeyForSeedPhraseExport() {
  return await authenticateWithPasskey(Action.EXPORT_SEED_PHRASE);
}
export async function signInWithPasskey() {
  try {
    const credential = await authenticateWithPasskey(Action.WALLET_SIGNIN);
    if (!credential) {
      return void 0;
    }
    const signInResp = await fetchWalletSigninRequest({ credential });
    if (signInResp?.walletAddress) {
      return signInResp.walletAddress;
    }
    return void 0;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "signInWithPasskey"
      }
    });
    throw error;
  }
}
export async function signMessagesWithPasskey(messages) {
  try {
    const credential = await authenticateWithPasskey(Action.SIGN_MESSAGES);
    const signedMessagesResp = await fetchSignMessagesRequest({ messages, credential });
    return signedMessagesResp.signedMessages;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "signMessagesWithPasskey"
      }
    });
    throw error;
  }
}
export async function signTransactionWithPasskey(transactions) {
  try {
    const credential = await authenticateWithPasskey(Action.SIGN_TRANSACTIONS);
    const signedTransactionResp = await fetchSignTransactionRequest({
      transactions,
      credential
    });
    return signedTransactionResp.signedTransactions;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "signTransactionWithPasskey"
      }
    });
    throw error;
  }
}
export async function signTypedDataWithPasskey(typedDataBatch) {
  try {
    const credential = await authenticateWithPasskey(Action.SIGN_TYPED_DATA_BATCH);
    const signedTypedDataResp = await fetchSignTypedDataRequest({
      typedDataBatch,
      credential
    });
    return signedTypedDataResp.signature;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "signTypedDataWithPasskey"
      }
    });
    throw error;
  }
}
export async function exportEncryptedSeedPhrase(encryptionKey) {
  try {
    const credential = await authenticateWithPasskey(Action.EXPORT_SEED_PHRASE);
    if (!credential) {
      return void 0;
    }
    const seedPhraseResp = await fetchExportSeedPhraseRequest({
      encryptionKey,
      credential
    });
    return seedPhraseResp.encryptedSeedPhrase;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "exportEncryptedSeedPhrase"
      }
    });
    throw error;
  }
}
export async function disconnectWallet() {
  try {
    await fetchDisconnectRequest();
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "disconnectWallet"
      }
    });
    throw error;
  }
}
export async function listAuthenticators() {
  try {
    const credential = await authenticateWithPasskey(Action.LIST_AUTHENTICATORS);
    const listAuthenticatorsResp = await fetchListAuthenticatorsRequest({ credential: JSON.stringify(credential) });
    return listAuthenticatorsResp.authenticators;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "listAuthenticators"
      }
    });
    throw error;
  }
}
export async function registerNewAuthenticator({
  authenticatorAttachment,
  existingCredential,
  username
}) {
  try {
    const newPasskeyCredential = await registerNewPasskey({
      authenticatorAttachment,
      action: Action.REGISTER_NEW_AUTHENTICATION_TYPES,
      username
    });
    if (newPasskeyCredential && existingCredential) {
      await fetchRegisterNewAuthenticatorRequest({
        newCredential: newPasskeyCredential,
        newAuthenticationType: AuthenticationTypes.PASSKEY_REGISTRATION,
        existingCredential,
        existingAuthenticationType: AuthenticationTypes.PASSKEY_AUTHENTICATION
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "registerNewAuthenticator"
      }
    });
    throw error;
  }
}
export async function deleteAuthenticator({
  authenticator,
  credential
}) {
  try {
    if (credential) {
      await fetchDeleteAuthenticatorRequest({
        credential,
        authenticationType: AuthenticationTypes.PASSKEY_AUTHENTICATION,
        authenticatorId: authenticator.id,
        authenticatorType: authenticator.type
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "embeddedWallet.ts",
        function: "deleteAuthenticator"
      }
    });
    throw error;
  }
}
